import type { AwaitingMission } from '../../hooks/useMissionsAwaitingHuman';
import { agoText } from '../bureau/age';
import { wakeText } from '../bureau/ottoNow';
import { freshSinceQuestionText } from './awaitingHuman';

/**
 * What a waiting question's line says about the facts that moved under it.
 *
 * Gilles, 08/10/2026, before « Un fait a changé : 1 facture émise · 2 brouillons archivés · 1
 * rendez-vous prévu »: « je suis censé faire quoi avec ça ? Si ça a changé, il devrait relancer la
 * machine et pas me l'afficher ». A moved fact now queues a reread (`missions_due_for_recheck`):
 * Otto keeps, rewords or retires his question. So the line says what OTTO does about it, and shows
 * the raw facts only where no reread will come — a deliverable, a mission at its wake-up cap
 * (`recheckable = false`). Once Otto has reread and kept the question, the facts he read are no
 * longer news: the server counts them from that reread (`seen_at`), and the line says so.
 */
export type QuestionFacts =
  /** Otto rereads, or will: `text` for the pane, `short` for a list line. */
  | { readonly kind: 'rereading'; readonly text: string; readonly short: string }
  /** Otto reread it since he asked, and kept it. */
  | { readonly kind: 'reread'; readonly text: string }
  /** What moved, as is: nobody will reread this question but Gilles. */
  | { readonly kind: 'facts'; readonly text: string };

type QuestionRow = Pick<
  AwaitingMission,
  | 'fresh_kinds'
  | 'fresh_last_from'
  | 'fresh_last_at'
  | 'stale_note'
  | 'recheck_at'
  | 'recheck_running'
  | 'rechecked_at'
  | 'recheckable'
>;

const BECAUSE = "il s'est passé quelque chose depuis qu'il l'a posée.";

/** Null when nothing moved and Otto never reread: the question speaks for itself. */
export function questionFacts(m: QuestionRow, timeZone: string, now: Date = new Date()): QuestionFacts | null {
  if (m.recheck_running) {
    return { kind: 'rereading', text: `Otto relit sa question : ${BECAUSE}`, short: 'Otto la relit' };
  }
  const queuedAt = m.recheck_at ? Date.parse(m.recheck_at) : Number.NaN;
  if (Number.isFinite(queuedAt) && queuedAt > now.getTime()) {
    const when = wakeText(m.recheck_at as string, now, timeZone);
    return { kind: 'rereading', text: `Otto relira sa question ${when} : ${BECAUSE}`, short: `Otto la relira ${when}` };
  }
  const facts = m.stale_note?.trim() || freshSinceQuestionText(m.fresh_kinds, m.fresh_last_from, m.fresh_last_at, timeZone);
  // Queued and due, or about to be queued by the runner's next pass (every quarter of an hour).
  if (Number.isFinite(queuedAt) || (facts && m.recheckable)) {
    return { kind: 'rereading', text: `Otto va relire sa question : ${BECAUSE}`, short: 'Otto va la relire' };
  }
  if (facts) return { kind: 'facts', text: facts };
  const reread = agoText(m.rechecked_at, now);
  return reread ? { kind: 'reread', text: `Otto l'a relue ${reread} : elle tient toujours.` } : null;
}
